import { useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useAvatar } from '@/hooks/useAvatar';
import { KBIcon } from '@/components/kbIcons';
import { personName } from '@/lib/people';
import type { ActesPerson as ActesProfile } from '@/integrations/actesApi';
import { actesEn } from '@/lib/actesCountry';

/**
 * A colleague, shown the way the portal shows colleagues: their photo when the
 * portal has it, their initials when it does not.
 *
 * The people of Actes ARE the people of the portal — same address, same face,
 * same name — so nothing here keeps a second identity: the Actes row only says
 * which profile this is, and who that person is comes from the portal's own
 * roster (which is also why somebody who left still has a name on their work).
 *
 * The photo is CONSTRAINED to the circle. It was not, and an avatar whose
 * image had no size painted itself across the whole table cell.
 */
/* Le nom d'une personne : celui du portail quand elle y est ; sinon celui de
   son profil ; sinon son adresse épelée. `personName` épèle aussi une adresse
   qu'il ne connaît pas — « Jean@gmail Com » —, et c'est ce qui s'affichait à
   la place du nom du profil. Une vraie fiche du portail n'a jamais d'@. */
export function nameOf(person: ActesProfile): string {
  const resolved = personName(person.email);
  if (resolved !== '—' && !resolved.includes('@')) return resolved;
  if (person.full_name) return person.full_name;
  const spelt = personName(person.email.split('@')[0]);
  return spelt !== '—' ? spelt : (person.email || '—');
}

export function ActesAvatar({ person, size = 'sm' }: { person?: ActesProfile | null; size?: 'sm' | 'md' }) {
  const email = person?.email ?? '';
  const photo = useAvatar(email);
  const name = person ? nameOf(person) : '';
  const initials = name.split(/[.\s@]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '·';
  return (
    /* `av` est la classe de la maquette : c'est elle qui donne la taille, le
       rond et le chevauchement dans une pile. `actes-av` reste parce que
       c'est ce composant-ci qui sait afficher la vraie photo. */
    <span className={`actes-av av ${size}`} title={name} aria-label={name}>
      {photo ? <img src={photo} alt="" /> : initials}
    </span>
  );
}

/** Avatar + name, where there is room for both. */
export function ActesWho({ person, hideName }: { person?: ActesProfile | null; hideName?: boolean }) {
  if (!person) return <span className="dim">—</span>;
  const name = nameOf(person);
  return (
    <span className="actes-who">
      <ActesAvatar person={person} />
      {!hideName && <span className="actes-who-n">{name}</span>}
    </span>
  );
}

/* ══ PLUSIEURS PERSONNES SUR UNE LIGNE ══
 *
 * Julien, 13 oct. 2026 : « si je suis avec Hector, est-ce qu'il est possible
 * que nos deux noms apparaissent ? ». Une DPC et une demande portent deux
 * colonnes : `assigned_to` (UNE personne, la principale) et
 * `assigned_emails` (TOUT LE MONDE, en adresses, depuis l'ancienne
 * application). On ne lisait que la première : 23 DPC et 465 demandes avaient
 * déjà deux ou trois personnes que l'écran ne montrait pas.
 *
 * On lit maintenant les deux, la principale d'abord. Une adresse sans profil
 * n'est jamais perdue : elle s'affiche épelée (« matilde.xavier » → « Matilde
 * Xavier »). Le serveur tient la même règle (server/services/actes/assignees.ts). */

/** Au-delà, ce n'est plus une équipe : c'est un copier-coller. Même borne que le serveur. */
export const MAX_ASSIGNEES = 8;

/** Ce qu'une DPC ou une demande dit de qui s'en occupe. */
export interface Assignable {
  assigned_to: string | null;
  assigned_emails?: string[] | null;
  /** L'adresse de la personne principale, envoyée par le serveur — utile
      quand son profil n'est plus actif et manque donc à la liste. */
  assignee_email?: string | null;
  assignee_name?: string | null;
}

const stranger = (email: string, id = '', name: string | null = null): ActesProfile =>
  ({ id, email, full_name: name, role: '', is_lawyer: false, is_active: false });

const fold = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Les lecteurs d'une ligne, construits une fois pour une liste de profils. */
export function peopleReader(profiles: ActesProfile[]) {
  const byId = new Map(profiles.map((p) => [p.id, p]));
  const byEmail = new Map(profiles.map((p) => [p.email.toLowerCase(), p]));
  /** Les adresses, la principale d'abord, sans doublon — ce qu'on renvoie au serveur. */
  const emails = (r: Assignable): string[] => {
    const main = r.assigned_to ? (byId.get(r.assigned_to)?.email ?? r.assignee_email ?? null) : null;
    const out: string[] = [];
    for (const v of [main, ...(r.assigned_emails ?? [])]) {
      const e = (v ?? '').trim().toLowerCase();
      if (e && !out.includes(e)) out.push(e);
    }
    return out;
  };
  /** Les personnes à montrer. */
  const people = (r: Assignable): ActesProfile[] => {
    const list = emails(r).map((e) => byEmail.get(e) ?? stranger(e));
    /* La principale dont on ne connaît pas l'adresse — un profil inactif sur
       une ligne qui n'a QUE `assigned_to` (le serveur écrit toujours la
       principale dans la liste) : gardée, sous le nom que la ligne porte. */
    if (r.assigned_to && !byId.has(r.assigned_to) && !r.assignee_email && !(r.assigned_emails ?? []).length) {
      list.unshift(stranger('', r.assigned_to, r.assignee_name ?? null));
    }
    return list;
  };
  /** Cette personne est-elle sur la ligne — principale OU à côté ? */
  const has = (r: Assignable, id: string): boolean => {
    if (r.assigned_to === id) return true;
    const e = byId.get(id)?.email.toLowerCase();
    return !!e && emails(r).includes(e);
  };
  return { emails, people, has };
}

/** Une pile de visages : trois au plus, puis « +N ». Le nom de chacun au
 *  survol de son visage ; ceux du « +N », au survol du « +N ». */
export function ActesStack({ people, max = 3 }: { people: ActesProfile[]; max?: number }) {
  const cut = people.length > max ? max - 1 : people.length;
  const rest = people.slice(cut);
  return (
    <span className="stack actes-stack" role="group" aria-label={people.map(nameOf).join(', ')}>
      {people.slice(0, cut).map((p) => <ActesAvatar key={p.email || p.id} person={p} />)}
      {rest.length > 0 && (
        <span className="actes-av av sm more" title={rest.map(nameOf).join(', ')}>+{rest.length}</span>
      )}
    </span>
  );
}

/** Le menu sort de la cellule ou du formulaire : fixé aux coordonnées du
 *  bouton et rendu sur <body>, pour que rien ne le rogne. Échap ferme. */
function usePopAt(btn: RefObject<HTMLElement | null>, open: boolean, onEscape: () => void) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const esc = useRef(onEscape);
  esc.current = onEscape;
  useEffect(() => {
    if (!open) { setAt(null); return; }
    const place = () => {
      const r = btn.current?.getBoundingClientRect();
      if (!r) return;
      const H = 340, W = 280;
      const below = window.innerHeight - r.bottom;
      setAt({
        top: below > H ? r.bottom + 6 : Math.max(8, r.top - H - 6),
        left: Math.min(Math.max(8, r.left), window.innerWidth - W - 8),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') esc.current(); };
    document.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('keydown', key);
    };
  }, [open, btn]);
  return at;
}

/**
 * L'AVATAR EST LE BOUTON.
 *
 * A face, a full name and a dropdown arrow, in a table column: three things
 * saying the same one. The face already identifies the person — that is what
 * a face is for — so it is the whole control: press it, choose. The name is
 * in the tooltip, where a name belongs when the picture is already there.
 *
 * PLUSIEURS VISAGES, UN SEUL BOUTON. La pile entière s'ouvre sur la liste de
 * l'équipe ; on coche et décoche, la première cochée est la principale, et
 * rien ne part avant « OK » (ou un clic à côté). Échap annule.
 *
 * Unassigned shows a dashed circle, because "nobody" has to look like a place
 * something is missing rather than like an empty cell.
 */
export function OwnerPicker({ people, value, canEdit, onChange, lang = 'fr' }: {
  /** Who may be chosen — the French team. */
  people: ActesProfile[];
  /** Who is on the row now, the lead first (`peopleReader().people`). */
  value: ActesProfile[];
  canEdit: boolean;
  /** Every address, the lead first. */
  onChange: (emails: string[]) => void;
  lang?: 'fr' | 'en';
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState<string[]>([]);
  const [order, setOrder] = useState<ActesProfile[]>([]);
  const btn = useRef<HTMLButtonElement>(null);
  const now = value.map((p) => p.email.toLowerCase()).filter(Boolean);
  const at = usePopAt(btn, open, () => setOpen(false));

  if (!canEdit) {
    return value.length
      ? <ActesStack people={value} />
      : <span className="actes-av none" title={T('Sans responsable', 'No owner')}>·</span>;
  }

  /* L'ordre de la liste est fixé à l'ouverture — ceux de la ligne d'abord —
     pour qu'une case cochée ne fasse pas sauter les autres sous le doigt. */
  const start = () => {
    const mine = new Set(now);
    setOrder([...value.filter((p) => p.email), ...people.filter((p) => !mine.has(p.email.toLowerCase()))]);
    setDraft(now); setQ(''); setOpen(true);
  };
  const finish = () => {
    setOpen(false);
    if (draft.join('|') !== now.join('|')) onChange(draft);
  };
  const toggle = (e: string) => setDraft((d) => (d.includes(e)
    ? d.filter((x) => x !== e)
    : d.length >= MAX_ASSIGNEES ? d : [...d, e]));

  /* Onze personnes tiennent dans un menu ; trente, non. On cherche. */
  const term = fold(q.trim());
  const shown = term ? order.filter((p) => fold(`${nameOf(p)} ${p.full_name ?? ''} ${p.email}`).includes(term)) : order;
  const names = value.map(nameOf).join(', ');

  return (
    <span className="actes-ownpick">
      <button ref={btn} type="button" className="actes-ownpick-b" aria-haspopup="listbox" aria-expanded={open}
        title={value.length ? `${names} — ${T('changer', 'change')}` : T('Assigner à quelqu’un', 'Assign to somebody')}
        onClick={(e) => { e.stopPropagation(); if (open) finish(); else start(); }}>
        {value.length ? <ActesStack people={value} /> : <span className="actes-av none">＋</span>}
      </button>
      {open && at && createPortal(
        <>
          <div className="actes-ownpick-scrim" onClick={finish} />
          <div className="actes-ownpick-pop multi actes-skin" role="listbox" aria-multiselectable="true" style={{ top: at.top, left: at.left }}>
            <div className="actes-ownpick-find">
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={T('Chercher quelqu’un…', 'Find somebody…')}
                aria-label={T('Chercher quelqu’un', 'Find somebody')}
              />
            </div>
            <div className="actes-ownpick-list">
              <button className="actes-combo-row" onClick={() => setDraft([])}>
                <span className="actes-av none">·</span>
                <span className="dim">{T('Personne', 'Nobody')}</span>
              </button>
              {shown.map((p) => {
                const e = p.email.toLowerCase();
                const at_ = draft.indexOf(e);
                return (
                  <button key={e} role="option" aria-selected={at_ >= 0}
                    className={`actes-combo-row ${at_ >= 0 ? 'on' : ''}`}
                    disabled={at_ < 0 && draft.length >= MAX_ASSIGNEES}
                    onClick={() => toggle(e)}>
                    <ActesAvatar person={p} />
                    <span>{nameOf(p)}</span>
                    {at_ === 0 && draft.length > 1 && <em>{T('principal(e)', 'lead')}</em>}
                    <span className="actes-ownpick-ck" aria-hidden>{at_ >= 0 ? <KBIcon name="check" size={12} /> : null}</span>
                  </button>
                );
              })}
              {shown.length === 0 && (
                <p className="actes-ownpick-none">{T('Personne ne correspond.', 'Nobody matches.')}</p>
              )}
            </div>
            <div className="actes-ownpick-foot">
              <span>{draft.length === 0 ? T('Personne', 'Nobody')
                : draft.length === 1 ? T('1 personne', '1 person')
                  : T(`${draft.length} personnes`, `${draft.length} people`)}</span>
              <button type="button" className="btn btn-sm btn-p" onClick={finish}>OK</button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </span>
  );
}

/**
 * LES PERSONNES, DANS UN FORMULAIRE.
 *
 * Une étiquette par personne — visage et nom en entier, puisque c'est
 * justement le nom que Julien voulait voir —, une croix pour la retirer, et
 * « Ajouter » qui ouvre l'équipe avec une recherche. La première est la
 * principale ; cliquer le nom d'une autre la met en premier.
 */
export function PeopleField({ people, value, onChange, lang = 'fr' }: {
  people: ActesProfile[];
  value: ActesProfile[];
  onChange: (next: ActesProfile[]) => void;
  lang?: 'fr' | 'en';
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const btn = useRef<HTMLButtonElement>(null);
  const at = usePopAt(btn, open, () => setOpen(false));
  const chosen = new Set(value.map((p) => p.email.toLowerCase()));
  const term = fold(q.trim());
  const offer = people
    .filter((p) => !chosen.has(p.email.toLowerCase()))
    .filter((p) => !term || fold(`${nameOf(p)} ${p.full_name ?? ''} ${p.email}`).includes(term));
  const same = (a: ActesProfile, b: ActesProfile) => (a.email || a.id) === (b.email || b.id);

  return (
    <div className="actes-picks actes-people">
      {value.map((p, i) => (
        <span key={p.email || p.id} className={`actes-pick ${i === 0 && value.length > 1 ? 'lead' : ''}`}>
          <ActesAvatar person={p} />
          {i === 0
            ? <span className="nm">{nameOf(p)}</span>
            : (
              <button type="button" className="nm" title={T('Mettre en premier', 'Make them the lead')}
                onClick={() => onChange([p, ...value.filter((x) => !same(x, p))])}>
                {nameOf(p)}
              </button>
            )}
          {i === 0 && value.length > 1 && (
            <em title={T('La personne principale — en tête de la colonne « Assigné à ».', 'The lead — first in the «Assigned to» column.')}>
              {T('principal(e)', 'lead')}
            </em>
          )}
          <button type="button" className="x" aria-label={T(`Retirer ${nameOf(p)}`, `Remove ${nameOf(p)}`)}
            onClick={() => onChange(value.filter((x) => !same(x, p)))}>×</button>
        </span>
      ))}
      {value.length < MAX_ASSIGNEES && (
        <button ref={btn} type="button" className="actes-pick-add" aria-haspopup="listbox" aria-expanded={open}
          onClick={() => { setQ(''); setOpen((o) => !o); }}>
          <KBIcon name="plus" size={12} /> {value.length ? T('Ajouter', 'Add') : T('Assigner', 'Assign')}
        </button>
      )}
      {open && at && createPortal(
        <>
          <div className="actes-ownpick-scrim" onClick={() => setOpen(false)} />
          <div className="actes-ownpick-pop multi actes-skin" role="listbox" style={{ top: at.top, left: at.left }}>
            <div className="actes-ownpick-find">
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={T('Chercher quelqu’un…', 'Find somebody…')}
                aria-label={T('Chercher quelqu’un', 'Find somebody')} />
            </div>
            <div className="actes-ownpick-list">
              {offer.map((p) => (
                <button key={p.id || p.email} type="button" role="option" aria-selected={false} className="actes-combo-row"
                  onClick={() => { onChange([...value, p]); setOpen(false); }}>
                  <ActesAvatar person={p} />
                  <span>{nameOf(p)}</span>
                </button>
              ))}
              {offer.length === 0 && (
                <p className="actes-ownpick-none">{T('Personne ne correspond.', 'Nobody matches.')}</p>
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
